import { AUTH_MESSAGES } from '@fos/shared/contracts/identity';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, errorMessage, validationIssues } from '../../../lib/api';
import { confirmStepUpCode, sendStepUpCode } from '../../../lib/auth';
import { CodeInput } from '../../../ui/CodeInput';
import { Field } from '../../../ui/Field';

/**
 * Kod z maila (Fala 7): wspólne klocki ekranu logowania i okien potwierdzenia (sejf, step-up `second_factor`).
 * Kod nigdy nie trafia do pamięci poza polem formularza (czyszczony po każdej próbie i zamknięciu).
 */

/** Sekundy do chwili `targetMs` (odświeżane co sekundę; 0 = już można). */
export function useCountdown(targetMs: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (targetMs === null || targetMs <= Date.now()) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [targetMs]);
  if (targetMs === null) return 0;
  return Math.max(0, Math.ceil((targetMs - now) / 1000));
}

/** Komunikat błędu kodu: zły (z liczbą prób), spalony, wygasły; inne błędy jak zwykle. */
export function codeErrorMessage(err: unknown): string {
  const details =
    err instanceof ApiError ? (err.details as { reason?: string; attemptsLeft?: number } | undefined) : undefined;
  if (details?.reason === 'invalid' && typeof details.attemptsLeft === 'number') {
    const n = details.attemptsLeft;
    const left = n === 1 ? 'Została 1 próba' : n >= 2 && n <= 4 ? `Zostały ${n} próby` : `Zostało ${n} prób`;
    return `${AUTH_MESSAGES.invalidCode}. ${left}.`;
  }
  const issue = validationIssues(err).find((i) => i.path === 'code');
  return issue?.message ?? errorMessage(err);
}

/** „Wyślij ponownie” z odliczaniem („Wyślij ponownie za 25 s”). */
export function ResendButton({
  availableAt,
  onResend,
  pending,
}: {
  availableAt: number | null;
  onResend: () => void;
  pending: boolean;
}) {
  const left = useCountdown(availableAt);
  const disabled = pending || left > 0;
  return (
    <button
      type="button"
      onClick={onResend}
      disabled={disabled}
      className="self-start rounded-xs text-caption font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent disabled:cursor-default disabled:text-fg-muted disabled:no-underline"
    >
      {left > 0 ? `Wyślij ponownie za ${left} s` : 'Wyślij ponownie'}
    </button>
  );
}

export interface StepUpCodeState {
  sent: boolean;
  email: string | null;
  code: string;
  setCode: (code: string) => void;
  error: string | null;
  pending: boolean;
  resendAvailableAt: number | null;
  send: () => Promise<void>;
  /** true = potwierdzono (sesja ma świeży kod); `override` = kod z onComplete (stan jeszcze bez ostatniej cyfry) */
  verify: (override?: string) => Promise<boolean>;
  reset: () => void;
}

/** Stan okna „Wyślij kod” → kod → potwierdzenie (POST /api/auth/step-up/code i …/verify). */
export function useStepUpCode(): StepUpCodeState {
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [resendAvailableAt, setResendAvailableAt] = useState<number | null>(null);

  const reset = useCallback(() => {
    setSent(false);
    setEmail(null);
    setCode('');
    setError(null);
    setPending(false);
    setResendAvailableAt(null);
  }, []);

  const send = useCallback(async () => {
    setPending(true);
    setError(null);
    try {
      const res = await sendStepUpCode();
      setSent(true);
      setEmail(res.email);
      setCode('');
      setResendAvailableAt(new Date(res.resendAvailableAt).getTime());
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RATE_LIMITED') {
        if (err.retryAfterSec !== undefined) setResendAvailableAt(Date.now() + err.retryAfterSec * 1000);
        setError(err.message);
      } else setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }, []);

  const verify = useCallback(
    async (override?: string) => {
      const value = override ?? code;
      if (!/^\d{6}$/.test(value)) {
        setError(AUTH_MESSAGES.codeFormat);
        return false;
      }
      setPending(true);
      setError(null);
      try {
        await confirmStepUpCode(value);
        setCode('');
        return true;
      } catch (err) {
        setCode('');
        setError(codeErrorMessage(err));
        return false;
      } finally {
        setPending(false);
      }
    },
    [code],
  );

  return { sent, email, code, setCode, error, pending, resendAvailableAt, send, verify, reset };
}

/** Pole kodu z podpowiedzią i „Wyślij ponownie” (po wysłaniu kodu). */
export function StepUpCodeFields({
  state,
  onComplete,
}: {
  state: StepUpCodeState;
  /** wszystkie cyfry wpisane (np. automatyczne potwierdzenie) */
  onComplete?: (code: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Field
        label="Kod z maila"
        error={state.error}
        hint={state.email ? `Wysłaliśmy 6-cyfrowy kod na ${state.email}. Kod jest ważny 10 minut.` : undefined}
      >
        <CodeInput
          value={state.code}
          onChange={state.setCode}
          onComplete={onComplete}
          autoFocus
          disabled={state.pending}
        />
      </Field>
      <ResendButton availableAt={state.resendAvailableAt} onResend={() => void state.send()} pending={state.pending} />
    </div>
  );
}
